import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storageService } from '@/services/local-storage';
import type { ScoreValue, CriteriaScore } from '@/types/evaluation.types';
import { calculateScorecardCompletion } from '@/services/scoring';
import { isScoringLocked } from '@/services/evaluation-status';
import { ScorecardHeader } from '@/components/scorecard/ScorecardHeader';
import { ScorecardProgressTabs } from '@/components/scorecard/ScorecardProgressTabs';
import { ScorecardTable } from '@/components/scorecard/ScorecardTable';
import { Card, CardContent } from '@/components/ui/card';
import { useEvaluation } from '@/hooks';

/**
 * Scorecard page for scoring solutions against evaluation criteria
 * Route: /evaluation/:evaluationId/scorecard/:solutionId/:participantId
 */
export function Scorecard() {
  const { evaluationId, solutionId, participantId } = useParams<{
    evaluationId: string;
    solutionId: string;
    participantId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['scorecard', 'common']);

  const { data: evaluation, isLoading } = useEvaluation(evaluationId || '');
  const [scores, setScores] = useState<Record<string, ScoreValue>>({});
  const [scrollToGroupId, setScrollToGroupId] = useState<string | null>(null);
  const [saveDebounce, setSaveDebounce] = useState<NodeJS.Timeout | null>(null);

  // Get participant information
  const participant = useMemo(() => {
    if (!evaluation || !participantId) return null;
    return evaluation.participants.find(p => p.id === participantId) || null;
  }, [evaluation, participantId]);

  // Load scores from scorecard when evaluation loads
  const scorecard = useMemo(() => {
    if (!evaluationId || !solutionId || !participantId) return null;
    return storageService.getScorecardBySolution(evaluationId, solutionId, participantId);
  }, [evaluationId, solutionId, participantId]); // evaluation not needed - scorecard doesn't depend on it

  const scorecardId = scorecard?.id || null;
  const isSubmitted = scorecard?.isSubmitted || false;

  // Check if scoring is locked due to evaluation status
  const isScoringLockedByStatus = evaluation ? isScoringLocked(evaluation.status) : false;
  const isLocked = isSubmitted || isScoringLockedByStatus;

  // Filter criteria groups based on participant assignments
  // Uses the same logic as scorecardCompletion.ts to ensure consistency
  // IMPORTANT: This must be before any early returns to satisfy React Hooks rules
  const visibleCriteriaGroups = useMemo(() => {
    if (!evaluation || !participant) return [];

    const assignedGroupIds = participant.criteriaGroupAssignments;

    if (assignedGroupIds === null) {
      // null = can score all groups (typical for Host)
      return evaluation.criteriaGroups;
    } else if (assignedGroupIds.length === 0) {
      // [] = cannot score any groups (Viewer - read-only)
      return [];
    } else {
      // Only assigned groups
      return evaluation.criteriaGroups.filter((group) =>
        assignedGroupIds.includes(group.id)
      );
    }
  }, [evaluation, participant]);

  // Initialize scores from scorecard
  useEffect(() => {
    if (scorecard) {
      const scoresMap: Record<string, ScoreValue> = {};
      scorecard.scores.forEach((s) => {
        scoresMap[s.criteriaId] = s.score;
      });
      setScores(scoresMap);
    } else {
      // Reset scores when switching to a solution with no scorecard
      setScores({});
    }
  }, [scorecard]);

  // Auto-save scores with debouncing (only if not locked)
  const saveScores = useCallback(() => {
    if (!evaluationId || !solutionId || !participantId || !participant || isLocked) return;

    // Convert scores map to array
    const scoresArray: CriteriaScore[] = Object.entries(scores).map(([criteriaId, score]) => ({
      criteriaId,
      score,
    }));

    storageService.saveScores(evaluationId, solutionId, scoresArray, participantId, participant.name);
  }, [evaluationId, solutionId, participantId, participant, scores, isLocked]); // scorecardId not needed

  // Debounced save when scores change
  useEffect(() => {
    if (saveDebounce) {
      clearTimeout(saveDebounce);
    }

    const timeout = setTimeout(() => {
      saveScores();
    }, 500);

    setSaveDebounce(timeout);

    return () => {
      if (saveDebounce) {
        clearTimeout(saveDebounce);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores]);

  const handleScoreChange = (criterionId: string, score: ScoreValue) => {
    // Optimistically update local state
    setScores((prev) => ({
      ...prev,
      [criterionId]: score,
    }));

    // Trigger mutation (will be debounced by the useEffect below)
  };

  const handleSolutionChange = (newSolutionId: string) => {
    navigate(`/evaluation/${evaluationId}/scorecard/${newSolutionId}/${participantId}`);
  };

  const handleGroupClick = (groupId: string) => {
    setScrollToGroupId(groupId);
  };

  const handleSubmit = () => {
    if (!scorecardId) return;

    storageService.submitScorecard(scorecardId);
    // Force reload to update isSubmitted state
    window.location.reload();
  };

  const handleReopen = () => {
    if (!scorecardId) return;

    storageService.reopenScorecard(scorecardId);
    // Force reload to update isSubmitted state
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Loading scorecard...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!evaluation || !solutionId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('scorecard:errors.notFound')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if solution exists
  const currentSolution = evaluation.targetSolutions.find((s) => s.id === solutionId);
  if (!currentSolution) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              {t('scorecard:errors.solutionNotFound')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate progress using filtered criteria groups
  const allCriteria = visibleCriteriaGroups.flatMap((g) => g.criteria);
  const scoredCriteria = allCriteria.filter((c) => {
    const score = scores[c.id];
    return score !== null && score !== undefined;
  });

  // Calculate per-group progress for visible (assigned) groups only
  const groupProgress = visibleCriteriaGroups.map((group) => {
    const groupCriteria = group.criteria;
    const groupScored = groupCriteria.filter((c) => {
      const score = scores[c.id];
      return score !== null && score !== undefined;
    });

    return {
      groupId: group.id,
      scored: groupScored.length,
      total: groupCriteria.length,
    };
  });

  // Calculate completion percentage for each solution for this participant
  const solutionCompletions = evaluation.targetSolutions.map((solution) => {
    const scorecard = participantId
      ? storageService.getScorecardBySolution(evaluation.id, solution.id, participantId)
      : null;
    if (!scorecard) {
      return {
        solutionId: solution.id,
        percentage: 0,
        scored: 0,
        total: allCriteria.length,
      };
    }

    const scoredCount = scorecard.scores.filter(
      (s) => s.score !== null && s.score !== undefined
    ).length;
    const percentage = allCriteria.length > 0
      ? Math.round((scoredCount / allCriteria.length) * 100)
      : 0;

    return {
      solutionId: solution.id,
      percentage,
      scored: scoredCount,
      total: allCriteria.length,
    };
  });

  // Calculate overall completion across all solutions for the current participant
  const overallCompletionInfo = participant
    ? calculateScorecardCompletion(
        participant,
        evaluation.id,
        evaluation.targetSolutions,
        evaluation.criteriaGroups
      )
    : { percentage: 0, scoredCount: 0, totalCount: allCriteria.length };

  return (
    <div className="min-h-screen bg-background">
      <ScorecardHeader
        evaluationId={evaluation.id}
        evaluationName={evaluation.name}
        evaluationStatus={evaluation.status}
        participantName={participant?.name || null}
        solutions={evaluation.targetSolutions}
        currentSolutionId={solutionId}
        onSolutionChange={handleSolutionChange}
        scoredCount={scoredCriteria.length}
        totalCount={allCriteria.length}
        solutionCompletions={solutionCompletions}
        overallCompletion={{
          percentage: overallCompletionInfo.percentage,
          scored: overallCompletionInfo.scoredCount,
          total: overallCompletionInfo.totalCount,
        }}
        isLocked={isLocked}
        isScoringLockedByStatus={isScoringLockedByStatus}
        canSubmit={!!scorecardId && !isScoringLockedByStatus}
        canReopen={!isScoringLockedByStatus}
        onSubmit={handleSubmit}
        onReopen={handleReopen}
      />

      <ScorecardProgressTabs
        criteriaGroups={visibleCriteriaGroups}
        groupProgress={groupProgress}
        activeGroupId={scrollToGroupId}
        onGroupClick={handleGroupClick}
      />

      <ScorecardTable
        criteriaGroups={visibleCriteriaGroups}
        scores={scores}
        onScoreChange={handleScoreChange}
        scrollToGroupId={scrollToGroupId}
        isLocked={isLocked}
      />
    </div>
  );
}
